import type { LanguageKey } from "@/providers/LocalizationProvider";

/**
 * Professional licence formats for the doctor registration wizard. Mirrors
 * psychometrist's LicenceRules, which re-checks everything on submit - this
 * copy only gives the doctor instant feedback.
 */

export type LicenceField = "number" | "issuer" | "state" | "npi" | "lanr";

export interface LicenceType {
  id: string;
  pattern: RegExp;
  /** Extra fields besides `number`; lanr is the only optional one. */
  fields: LicenceField[];
}

export interface Licence {
  country?: string | null;
  type?: string | null;
  number?: string | null;
  issuer?: string | null;
  state?: string | null;
  npi?: string | null;
  lanr?: string | null;
}

export const OTHER_COUNTRY = "OTHER";

export const LICENCE_TYPES: Record<string, LicenceType[]> = {
  IR: [
    { id: "ir_medical_council", pattern: /^\d{3,8}$/, fields: [] },
    { id: "ir_psychology_org", pattern: /^\d{3,10}$/, fields: [] },
  ],
  DE: [{ id: "de_approbation", pattern: /^[A-Z0-9/]{3,40}$/, fields: ["issuer", "lanr"] }],
  US: [{ id: "us_state_license", pattern: /^[A-Z0-9]{3,20}$/, fields: ["state", "npi"] }],
  FR: [
    { id: "fr_rpps", pattern: /^\d{11}$/, fields: [] },
    { id: "fr_adeli", pattern: /^\d{9}$/, fields: [] },
  ],
  IQ: [{ id: "iq_syndicate", pattern: /^[A-Z0-9/]{2,40}$/, fields: ["issuer"] }],
  [OTHER_COUNTRY]: [{ id: "other", pattern: /^[A-Z0-9/]{2,40}$/, fields: ["issuer"] }],
};

/** Countries offered in the wizard, in display order. */
export const LICENCE_COUNTRIES = ["IQ", "DE", "US", "FR", "IR", OTHER_COUNTRY] as const;

/** English names, used when a locale lacks join.country.*. */
export const LICENCE_COUNTRY_NAMES: Record<string, string> = {
  IR: "Iran",
  IQ: "Kurdistan",
  DE: "Germany",
  US: "United States",
  FR: "France",
  [OTHER_COUNTRY]: "Another country",
};

/**
 * Where a doctor using this UI language most likely practises. Only a
 * default - a German-speaking doctor in Iran just picks Iran.
 */
export const DEFAULT_COUNTRY_BY_LANGUAGE: Record<LanguageKey, string> = {
  fa: "IR",
  ckb: "IQ",
  kmr: "IQ",
  de: "DE",
  en: "US",
  fr: "FR",
};

export const US_STATES = [
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "DC", "FL", "GA", "HI", "ID", "IL", "IN", "IA", "KS",
  "KY", "LA", "ME", "MD", "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ", "NM", "NY", "NC",
  "ND", "OH", "OK", "OR", "PA", "PR", "RI", "SC", "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY",
];

const DIGITS: Record<string, string> = {
  "۰": "0", "۱": "1", "۲": "2", "۳": "3", "۴": "4", "۵": "5", "۶": "6", "۷": "7", "۸": "8", "۹": "9",
  "٠": "0", "١": "1", "٢": "2", "٣": "3", "٤": "4", "٥": "5", "٦": "6", "٧": "7", "٨": "8", "٩": "9",
};

/** Persian/Arabic digits to ASCII, separators dropped, upper-cased. */
export function normalizeLicenceValue(value: string | null | undefined): string {
  return (value ?? "")
    .replace(/[۰-۹٠-٩]/g, (d) => DIGITS[d])
    .replace(/[\s\-.]+/g, "")
    .toUpperCase();
}

/** Country key into LICENCE_TYPES; unlisted countries use OTHER. */
export function licenceCountryKey(country: string | null | undefined): string {
  const key = (country ?? "").toUpperCase();
  return key in LICENCE_TYPES ? key : OTHER_COUNTRY;
}

export function licenceTypesFor(country: string | null | undefined): LicenceType[] {
  return LICENCE_TYPES[licenceCountryKey(country)];
}

/** NPI check digit: Luhn over the number prefixed with 80840. */
export function isValidNpi(npi: string): boolean {
  if (!/^\d{10}$/.test(npi)) {
    return false;
  }
  let sum = 0;
  const digits = `80840${npi}`;
  for (let i = digits.length - 1, double = false; i >= 0; i--, double = !double) {
    let d = Number(digits[i]);
    if (double) {
      d *= 2;
      if (d > 9) d -= 9;
    }
    sum += d;
  }
  return sum % 10 === 0;
}

/** Fields that are missing or malformed, keyed the same way the API reports them. */
export function validateLicence(licence: Licence): LicenceField[] | ["country"] | ["type"] {
  if (!licence.country) {
    return ["country"];
  }
  const type = licenceTypesFor(licence.country).find((t) => t.id === licence.type);
  if (!type) {
    return ["type"];
  }

  const errors: LicenceField[] = [];
  if (!type.pattern.test(normalizeLicenceValue(licence.number))) {
    errors.push("number");
  }
  if (type.fields.includes("issuer") && !licence.issuer?.trim()) {
    errors.push("issuer");
  }
  if (type.fields.includes("state") && !US_STATES.includes((licence.state ?? "").toUpperCase())) {
    errors.push("state");
  }
  if (type.fields.includes("npi") && !isValidNpi(normalizeLicenceValue(licence.npi))) {
    errors.push("npi");
  }
  if (type.fields.includes("lanr") && licence.lanr?.trim() && !/^\d{9}$/.test(normalizeLicenceValue(licence.lanr))) {
    errors.push("lanr");
  }
  return errors;
}

// ISO 3166-1 alpha-2 codes, for "another country". Names come from the
// browser (Intl.DisplayNames) in the visitor's language, so no translations
// to maintain.
const ISO_COUNTRIES = (
  "AD AE AF AG AI AL AM AO AQ AR AS AT AU AW AX AZ BA BB BD BE BF BG BH BI BJ BL BM BN BO BQ BR BS BT BV BW BY BZ " +
  "CA CC CD CF CG CH CI CK CL CM CN CO CR CU CV CW CX CY CZ DJ DK DM DO DZ EC EE EG EH ER ES ET FI FJ FK FM FO " +
  "GA GB GD GE GF GG GH GI GL GM GN GP GQ GR GS GT GU GW GY HK HM HN HR HT HU ID IE IL IM IN IO IS IT JE JM JO JP " +
  "KE KG KH KI KM KN KP KR KW KY KZ LA LB LC LI LK LR LS LT LU LV LY MA MC MD ME MF MG MH MK ML MM MN MO MP MQ MR MS MT MU MV MW MX MY MZ " +
  "NA NC NE NF NG NI NL NO NP NR NU NZ OM PA PE PF PG PH PK PL PM PN PR PS PT PW PY QA RE RO RS RU RW " +
  "SA SB SC SD SE SG SH SI SJ SK SL SM SN SO SR SS ST SV SX SY SZ TC TD TF TG TH TJ TK TL TM TN TO TR TT TV TW TZ " +
  "UA UG UM UY UZ VA VC VE VG VI VN VU WF WS YE YT ZA ZM ZW"
).split(" ");

/** Emoji flag for an ISO code (regional indicator pair). */
export function flagEmoji(code: string): string {
  return String.fromCodePoint(...[...code.toUpperCase()].map((c) => 0x1f1e6 + c.charCodeAt(0) - 65));
}

/**
 * Countries for "another country": every ISO country except the ones offered
 * as their own choice, named in the visitor's language and sorted by name.
 */
export function otherLicenceCountries(language: string): { value: string; label: string; flag: string }[] {
  let names: Intl.DisplayNames | null = null;
  try {
    names = new Intl.DisplayNames([language, "en"], { type: "region" });
  } catch {
    // Very old browsers: show the codes.
  }
  const offered = new Set<string>(LICENCE_COUNTRIES);
  return ISO_COUNTRIES.filter((code) => !offered.has(code))
    .map((code) => ({ value: code, label: names?.of(code) ?? code, flag: flagEmoji(code) }))
    .sort((a, b) => a.label.localeCompare(b.label, language));
}
